/**
 * Maps API errors to friendly, user-readable messages.
 * Never exposes raw HTTP status text or internal error details.
 */

const STATUS_MESSAGES: Record<number, string> = {
  400: 'The request was invalid. Please check your input and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You don\'t have permission to do that.',
  404: 'The requested item could not be found.',
  409: 'This already exists. Please try a different value.',
  413: 'The file is too large to upload.',
  422: 'The information provided is invalid. Please check and try again.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Something went wrong on our end. Please try again shortly.',
  502: 'Service is temporarily unavailable. Please try again shortly.',
  503: 'Service is temporarily unavailable. Please try again shortly.',
};

// Maps specific backend error messages to friendly ones
const MESSAGE_MAP: Array<[RegExp, string]> = [
  [/already exists/i,                       'An account with this email already exists.'],
  [/invalid.*credentials?/i,                'Incorrect email or password.'],
  [/invalid.*email.*or.*password/i,         'Incorrect email or password.'],
  [/invalid.*password/i,                    'Incorrect email or password.'],
  [/user not found/i,                       'No account found with that email address.'],
  [/email.*not.*verified/i,                 'Please verify your email address before signing in.'],
  [/verify.*email.*before/i,                'Please verify your email address before signing in.'],
  [/token.*expired/i,                       'This link has expired. Please request a new one.'],
  [/invalid.*token/i,                       'This link is invalid or has already been used.'],
  [/password.*too.*weak/i,                  'Please choose a stronger password.'],
  [/email.*invalid/i,                       'Please enter a valid email address.'],
  [/permission.*denied/i,                   'You don\'t have permission to do that.'],
  [/document.*not.*found/i,                 'This document could not be found.'],
  [/folder.*not.*found/i,                   'This folder could not be found.'],
  [/file.*too.*large/i,                     'The file is too large. Please upload a smaller file.'],
  [/invalid.*pdf/i,                         'The file format is not supported or the file is corrupted.'],
  [/too many.*attempts?/i,                  'Too many sign-in attempts. Please wait a few minutes and try again.'],
  [/network error/i,                        'Unable to connect. Please check your internet connection.'],
  [/timeout/i,                              'The request timed out. Please try again.'],
];

export function getErrorMessage(err: any, fallback?: string): string {
  // Network error (no response)
  if (!err.response) {
    if (err.message?.toLowerCase().includes('network')) {
      return 'Unable to connect. Please check your internet connection.';
    }
    if (err.message?.toLowerCase().includes('timeout')) {
      return 'The request timed out. Please try again.';
    }
    // Client-side rate limit thrown before HTTP call
    if (err.message) {
      for (const [pattern, friendly] of MESSAGE_MAP) {
        if (pattern.test(err.message)) return friendly;
      }
    }
    return fallback || 'Something went wrong. Please try again.';
  }

  const status: number = err.response?.status;
  const serverMessage: string = err.response?.data?.message || err.response?.data?.error || '';

  // Try to match the server message to a friendly one first
  if (serverMessage) {
    for (const [pattern, friendly] of MESSAGE_MAP) {
      if (pattern.test(serverMessage)) return friendly;
    }
  }

  // Context-aware status code mapping
  if (status === 401) {
    // During login the 401 means wrong credentials, not an expired session
    return serverMessage || 'Incorrect email or password.';
  }
  if (status === 403) {
    // Could be unverified email or permission denied — prefer server message
    return serverMessage || 'You don\'t have permission to do that.';
  }

  // Fall back to status code mapping
  if (status && STATUS_MESSAGES[status]) {
    return STATUS_MESSAGES[status];
  }

  return fallback || 'Something went wrong. Please try again.';
}
